// ─── src/CatalystPortalView.tsx ───────────────────────────────────────────────
//
// The entire dashboard experience for a logged-in catalyst. Distinct from the
// admin views — a catalyst sees only their own meetups (as host or co-host),
// their attendees, and can approve/reject + scan check-ins for those events
// only. Everything else in the admin dashboard is hidden.

import { useMemo, useState } from "react";
import {
  Calendar, Clock, MapPin, Users, CheckCircle, XCircle, UserCheck, Undo2,
  Eye, X, QrCode, FileText, Pencil,
} from "lucide-react";
import { EditEventModal } from "./components/dashboard/EditEventModal";
import { EditProposalModal } from "./components/dashboard/EditProposalModal";
import { useAuth } from "./auth/AuthContext";
import {
  useCatalystEvents,
  useCatalystEventAttendees,
  useCatalystProposals,
  useCatalystApproveAttendee,
  useCatalystCheckInById,
  useCatalystUndoCheckIn,
  useCatalystUpdateEvent,
  useCatalystUpdateProposal,
} from "./hooks/useCatalystPortal";
import type {
  CatalystAttendee,
  CatalystEvent,
  CatalystProposal,
} from "./api/catalystPortal";
import { CatalystCheckInScanner } from "./components/dashboard/CatalystCheckInScanner";
import { DoorCodesPanel } from "./components/dashboard/DoorCodesPanel";
import { catalystDoorCodesApi } from "./api/doorCodes";
import { TableSkeleton } from "./components/ui/Skeleton";
import { StatCard } from "./components/ui/StatCard";
import { CatalystFeedbackPanel } from "./components/gewFeedback/CatalystFeedbackPanel";

/** The GEW feedback form covers meetups held during GEW 2026 (1–5 Nov). */
const inGewWindow = (date: string) => date.slice(0, 10) >= "2026-11-01" && date.slice(0, 10) <= "2026-11-05";

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}
function formatTime(iso?: string | null) {
  if (!iso) return "";
  return new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
}

const PROPOSAL_LABEL: Record<string, string> = {
  pending:  "Waiting for Approval",
  accepted: "Published",
  rejected: "Rejected",
};

// ─────────────────────────────────────────────────────────────────────────────

export default function CatalystPortalView() {
  const { user } = useAuth();
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [showScanner, setShowScanner] = useState(false);
  const [detailAttendee, setDetailAttendee] = useState<CatalystAttendee | null>(null);
  const [editingEvent, setEditingEvent] = useState<CatalystEvent | null>(null);
  const [editingProposal, setEditingProposal] = useState<CatalystProposal | null>(null);

  const { data: events = [], isLoading: eventsLoading } = useCatalystEvents();
  const { data: proposals = [] } = useCatalystProposals();

  // Auto-select the first upcoming event so the page isn't empty on arrival.
  const defaultEventId = useMemo(
    () => events.find((e) => e.when === "upcoming")?.id ?? events[0]?.id ?? null,
    [events],
  );
  const activeEventId = selectedEventId ?? defaultEventId;
  const activeEvent = events.find((e) => e.id === activeEventId) ?? null;

  const { data: attendees = [], isLoading: attendeesLoading } = useCatalystEventAttendees(
    activeEventId ?? "",
    {},
    !!activeEventId,
  );

  const { mutate: approve, isPending: isApproving } = useCatalystApproveAttendee();
  const { mutate: checkIn, isPending: isCheckingIn } = useCatalystCheckInById();
  const { mutate: undoCheckIn, isPending: isUndoing } = useCatalystUndoCheckIn();
  const { mutateAsync: updateEvent, isPending: isUpdatingEvent } = useCatalystUpdateEvent();
  const { mutateAsync: updateProposal, isPending: isUpdatingProposal } = useCatalystUpdateProposal();

  const pendingReview = attendees.filter((a) => a.approvalStatus === "pending").length;
  const attendedCount = attendees.filter((a) => !!a.checkedInAt).length;

  // ── Empty state ────────────────────────────────────────────────────────────
  if (!eventsLoading && events.length === 0 && proposals.length === 0) {
    return (
      <div className="space-y-6">
        <Header displayName={user?.label ?? ""} />
        <div className="card p-10 text-center">
          <p className="text-white font-semibold mb-1">Nothing here yet</p>
          <p className="text-brand-muted text-sm">
            When you submit a meetup proposal on the website, it&rsquo;ll appear here for you
            to manage.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Header displayName={user?.label ?? ""} />

      {/* Proposals — small strip up top; only meaningful for pending ones */}
      {proposals.length > 0 && (
        <div className="card p-5">
          <p className="text-white font-semibold text-sm mb-3 flex items-center gap-2">
            <FileText size={14} className="text-brand-green" /> Your proposals
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {proposals.map((p) => (
              <div key={p.id} className="rounded-lg border border-brand-border p-3">
                <p className="text-white text-sm truncate" dir="rtl">{p.eventFormat}</p>
                <p className="text-brand-muted text-xs mt-0.5">
                  {formatDate(p.proposedDate)} · {p.venue}
                </p>
                <div className="flex items-center justify-between mt-2 gap-2">
                  <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] ${
                    p.status === "accepted" ? "bg-emerald-500/15 text-emerald-400"
                    : p.status === "rejected" ? "bg-red-500/15 text-red-400"
                    : "bg-amber-500/15 text-amber-400"
                  }`}>
                    {PROPOSAL_LABEL[p.status] ?? p.status}
                  </span>
                  {p.status === "pending" && (
                    <button
                      onClick={() => setEditingProposal(p)}
                      className="p-1 rounded-md text-brand-muted hover:text-white hover:bg-brand-border transition-colors"
                      title="Edit proposal"
                    >
                      <Pencil size={13} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Event picker */}
      {events.length > 0 && (
        <div className="card p-5">
          <p className="text-white font-semibold text-sm mb-3">Your meetups</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {events.map((e) => (
              <EventPickCard
                key={e.id}
                event={e}
                selected={e.id === activeEventId}
                onClick={() => { setSelectedEventId(e.id); setShowScanner(false); }}
              />
            ))}
          </div>
        </div>
      )}

      {activeEvent && (
        <>
          {/* Active-event header with edit action */}
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-white font-semibold truncate">{activeEvent.title}</p>
              <p className="text-brand-muted text-xs">
                {formatDate(activeEvent.eventDate)} · {activeEvent.startTime} · {activeEvent.venue}
              </p>
            </div>
            <button
              onClick={() => setEditingEvent(activeEvent)}
              className="btn-ghost text-sm shrink-0 inline-flex items-center gap-1.5"
            >
              <Pencil size={14} /> Edit meetup
            </button>
          </div>

          {/* Stats for the selected event */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            <StatCard label="Capacity" value={activeEvent.capacity} icon={Users} accent="bg-brand-green" />
            <StatCard label="Registered" value={activeEvent.confirmedCount} icon={CheckCircle} accent="bg-brand-green" />
            <StatCard label="Waitlisted" value={activeEvent.waitlistCount} icon={Clock} accent="bg-brand-teal" />
            <StatCard label="Pending Review" value={pendingReview} icon={Clock} accent="bg-brand-teal" />
            <StatCard label="Attended" value={attendedCount} icon={UserCheck} accent="bg-brand-green" />
          </div>

          {/* Door check-in toggle */}
          <div className="flex items-center justify-between gap-3 card p-4">
            <div>
              <p className="text-white text-sm font-semibold flex items-center gap-2">
                <QrCode size={15} className="text-brand-green" /> Door check-in
              </p>
              <p className="text-brand-muted text-xs mt-0.5">
                Scan attendee QR codes to mark them as attended.
              </p>
            </div>
            <button
              onClick={() => setShowScanner((v) => !v)}
              className={showScanner ? "btn-ghost text-sm shrink-0" : "btn-primary text-sm shrink-0"}
            >
              {showScanner ? "Close scanner" : "Open scanner"}
            </button>
          </div>

          {showScanner && <CatalystCheckInScanner eventId={activeEvent.id} />}

          <DoorCodesPanel
            queryKey={["catalyst", "door-codes", activeEvent.id]}
            list={() => catalystDoorCodesApi.list(activeEvent.id)}
            create={(label) => catalystDoorCodesApi.create(activeEvent.id, label)}
            revoke={catalystDoorCodesApi.revoke}
          />

          {inGewWindow(activeEvent.eventDate) && <CatalystFeedbackPanel eventId={activeEvent.id} />}

          {/* Attendee list */}
          {attendeesLoading ? (
            <TableSkeleton />
          ) : (
            <AttendeesTable
              rows={attendees}
              onApprove={(id) => approve({ id, accepted: true })}
              onReject={(id) => approve({ id, accepted: false })}
              onCheckIn={(id) => checkIn(id)}
              onUndoCheckIn={(id) => undoCheckIn(id)}
              onView={setDetailAttendee}
              isBusy={isApproving || isCheckingIn || isUndoing}
            />
          )}
        </>
      )}

      {detailAttendee && <DetailModal row={detailAttendee} onClose={() => setDetailAttendee(null)} />}
      {editingEvent && (
        <EditEventModal
          event={editingEvent}
          onClose={() => setEditingEvent(null)}
          onSave={async (payload) => {
            await updateEvent({ eventId: editingEvent.id, payload });
            setEditingEvent(null);
          }}
          saving={isUpdatingEvent}
        />
      )}
      {editingProposal && (
        <EditProposalModal
          proposal={editingProposal}
          onClose={() => setEditingProposal(null)}
          onSave={async (payload) => {
            await updateProposal({ proposalId: editingProposal.id, payload });
            setEditingProposal(null);
          }}
          saving={isUpdatingProposal}
        />
      )}
    </div>
  );
}

// ── Header ────────────────────────────────────────────────────────────────────

function Header({ displayName }: { displayName: string }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <div>
        <p className="text-white text-lg font-semibold" dir="rtl">أهلاً، {displayName}</p>
        <p className="text-brand-muted text-sm">Your meetups and attendees.</p>
      </div>
    </div>
  );
}

// ── Event card ────────────────────────────────────────────────────────────────

function EventPickCard({
  event, selected, onClick,
}: { event: CatalystEvent; selected: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={`text-left rounded-xl border overflow-hidden transition-colors ${
        selected
          ? "border-brand-green ring-1 ring-brand-green bg-brand-green/5"
          : "border-brand-border hover:border-brand-green/40"
      }`}
    >
      <div className="flex gap-3 p-3">
        <img
          src={event.imageUrl}
          alt=""
          className="w-16 h-16 rounded-lg object-cover bg-brand-navy shrink-0"
        />
        <div className="min-w-0 flex-1">
          <p className="text-white text-sm font-medium truncate">{event.title}</p>
          <p className="text-brand-muted text-xs mt-0.5 flex items-center gap-1">
            <Calendar size={11} /> {formatDate(event.eventDate)} · {event.startTime}
          </p>
          <p className="text-brand-muted text-xs mt-0.5 flex items-center gap-1 truncate">
            <MapPin size={11} className="shrink-0" /> {event.venue}
          </p>
          <p className="text-xs mt-1">
            <span className="text-brand-muted">{event.confirmedCount}/{event.capacity}</span>
            <span className={`ml-2 px-1.5 py-0.5 rounded text-[10px] ${
              event.when === "past" ? "bg-brand-border text-brand-muted"
              : event.attendeeStatus === "Open" ? "bg-emerald-500/15 text-emerald-400"
              : "bg-amber-500/15 text-amber-400"
            }`}>
              {event.when === "past" ? "Past" : event.attendeeStatus}
            </span>
          </p>
        </div>
      </div>
    </button>
  );
}

// ── Attendee table ────────────────────────────────────────────────────────────

interface AttendeesTableProps {
  rows: CatalystAttendee[];
  onApprove: (id: string) => void;
  onReject: (id: string) => void;
  onCheckIn: (id: string) => void;
  onUndoCheckIn: (id: string) => void;
  onView: (a: CatalystAttendee) => void;
  isBusy: boolean;
}

function AttendeesTable({ rows, onApprove, onReject, onCheckIn, onUndoCheckIn, onView, isBusy }: AttendeesTableProps) {
  if (rows.length === 0) {
    return <div className="card p-10 text-center text-brand-muted">No RSVPs for this event yet.</div>;
  }

  return (
    <div className="card overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-brand-border">
              <th className="px-4 py-3 text-left text-brand-muted font-medium">#</th>
              <th className="px-4 py-3 text-left text-brand-muted font-medium">Name</th>
              <th className="px-4 py-3 text-left text-brand-muted font-medium">Contact</th>
              <th className="px-4 py-3 text-left text-brand-muted font-medium">Seat</th>
              <th className="px-4 py-3 text-left text-brand-muted font-medium">Review</th>
              <th className="px-4 py-3 text-left text-brand-muted font-medium">Attended</th>
              <th className="px-4 py-3 text-left text-brand-muted font-medium">Feedback</th>
              <th className="px-4 py-3 text-left text-brand-muted font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((a, idx) => (
              <tr key={a.id} className="border-b border-brand-border/50 hover:bg-brand-border/20 transition-colors">
                <td className="px-4 py-3 text-brand-muted">{idx + 1}</td>
                <td className="px-4 py-3 text-white font-medium whitespace-nowrap" dir="rtl">{a.fullName}</td>
                <td className="px-4 py-3 text-brand-muted">
                  <div className="whitespace-nowrap">{a.email}</div>
                  <div className="text-xs" dir="ltr">{a.whatsapp}</div>
                </td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-0.5 rounded-full text-xs capitalize ${
                    a.status === "confirmed" ? "bg-emerald-500/15 text-emerald-400" : "bg-amber-500/15 text-amber-400"
                  }`}>{a.status === "confirmed" ? "registered" : a.status}</span>
                </td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-0.5 rounded-full text-xs capitalize ${
                    a.approvalStatus === "accepted" ? "bg-emerald-500/15 text-emerald-400"
                    : a.approvalStatus === "rejected" ? "bg-red-500/15 text-red-400"
                    : "bg-brand-border text-brand-muted"
                  }`}>{a.approvalStatus}</span>
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  {a.checkedInAt ? (
                    <span className="inline-flex items-center gap-1.5 text-emerald-400 text-xs">
                      <UserCheck size={13} /> {formatTime(a.checkedInAt)}
                    </span>
                  ) : a.approvalStatus === "accepted" ? (
                    <button onClick={() => onCheckIn(a.id)} disabled={isBusy} title="Mark as attended"
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium border border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10 transition-colors disabled:opacity-40">
                      <UserCheck size={13} /> Check in
                    </button>
                  ) : (
                    <span className="text-brand-muted text-xs">—</span>
                  )}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-xs">
                  {a.feedbackSubmittedAt ? (
                    <span className="text-emerald-400">Answered</span>
                  ) : a.feedbackSentAt ? (
                    <span className="text-brand-muted">Emailed</span>
                  ) : (
                    <span className="text-brand-muted">—</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1">
                    <button onClick={() => onView(a)} title="View details"
                      className="p-1.5 rounded-lg text-brand-muted hover:text-white hover:bg-brand-border transition-colors">
                      <Eye size={14} />
                    </button>
                    {a.approvalStatus !== "accepted" && (
                      <button onClick={() => onApprove(a.id)} disabled={isBusy} title="Accept"
                        className="p-1.5 rounded-lg text-brand-muted hover:text-emerald-400 hover:bg-emerald-500/10 transition-colors disabled:opacity-40">
                        <CheckCircle size={14} />
                      </button>
                    )}
                    {a.approvalStatus !== "rejected" && (
                      <button onClick={() => onReject(a.id)} disabled={isBusy} title="Reject (frees seat)"
                        className="p-1.5 rounded-lg text-brand-muted hover:text-amber-400 hover:bg-amber-500/10 transition-colors disabled:opacity-40">
                        <XCircle size={14} />
                      </button>
                    )}
                    {a.checkedInAt && (
                      <button onClick={() => onUndoCheckIn(a.id)} disabled={isBusy} title="Undo check-in"
                        className="p-1.5 rounded-lg text-brand-muted hover:text-white hover:bg-brand-border transition-colors disabled:opacity-40">
                        <Undo2 size={14} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Detail modal (read-only for catalysts) ────────────────────────────────────

function Field({ label, value, rtl }: { label: string; value?: string | null; rtl?: boolean }) {
  if (!value) return null;
  return (
    <div>
      <p className="text-brand-muted text-xs uppercase tracking-wider mb-1">{label}</p>
      <p className="text-white text-sm whitespace-pre-wrap leading-relaxed" dir={rtl ? "rtl" : "ltr"}>{value}</p>
    </div>
  );
}

function DetailModal({ row, onClose }: { row: CatalystAttendee; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div className="card w-full max-w-2xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-brand-border">
          <div dir="rtl">
            <p className="text-white font-bold text-lg">{row.fullName}</p>
            <p className="text-brand-muted text-sm" dir="ltr">{row.email}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg text-brand-muted hover:text-white hover:bg-brand-border transition-colors">
            <X size={16} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-6">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Field label="Gender" value={row.gender} rtl />
            <Field label="Age group" value={row.ageGroup} rtl />
            <Field label="WhatsApp" value={row.whatsapp} />
            <Field label="Current status" value={row.currentStatus} rtl />
          </div>
          <div className="pt-4 border-t border-brand-border grid grid-cols-2 gap-4">
            <Field label="Governorate" value={row.governorate} rtl />
            <Field label="City / area" value={row.cityArea} rtl />
            <Field label="LinkedIn" value={row.linkedInUrl} />
          </div>
          <div className="pt-4 border-t border-brand-border">
            <p className="text-brand-muted text-xs uppercase tracking-wider mb-2">Skills</p>
            <div className="flex flex-wrap gap-2" dir="rtl">
              {row.skills.map((s) => (
                <span key={s} className="px-2.5 py-1 rounded-lg text-xs bg-brand-teal/15 text-brand-tealLight">{s}</span>
              ))}
            </div>
          </div>
          <div className="pt-4 border-t border-brand-border space-y-4">
            <Field label="Why they want to attend" value={row.whyAttend} rtl />
            <Field label="Questions for the event" value={row.questionsForEvent} rtl />
          </div>
        </div>
      </div>
    </div>
  );
}

// Edit meetup modal is imported from components/dashboard/EditEventModal —
// shared with the master dashboard's MeetupAttendeesView.
